import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { Card, CardContent } from "@babackapp/ui/components/card";
import useUpdatePortalBasics from "@babackapp/private-api-client/client/use-update-portal-basics";
import CustomerPortalBasicsForm from "./customer-portal-basics-form.tsx";
import { CustomerPortalFormValues } from "./use-customer-portal-basics-form.ts";
import { CustomerSegmentsForm } from "./customer-segments-form.tsx";
import { SettingsFormSectionHeader } from "@babackapp/react-hook-forms-components/form-page";
import useTranslation from "@babackapp/translations/use-translation";
import { LockClosedIcon } from "@heroicons/react/24/solid";

export default function CustomerPortalBasicsCard() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: update } = useUpdatePortalBasics(shop);
  const isRetractionOnly = useIsRetractionOnly();

  const onSubmit = (data: CustomerPortalFormValues) => {
    return update({
      contactLink: data.contactLink,
      languages: data.languages,
      webOrderOnly: data.webOrderOnly,
      nonReturnableMetafieldItems: data.nonReturnableMetafieldItems,
      tagsForNotAcceptedOrder: data.tagsForNotAcceptedOrder,
      nonReturnableItems: data.nonReturnableItems,
      restockOnReturn: data.restockOnReturn,
      restockLocationId: data.restockLocationId ?? undefined,
      autoTreateOnLogisticianValidate: data.autoTreateOnLogisticianValidate,
      clientCanUpdatePersonalInformation:
        data.clientCanUpdatePersonalInformation,
      canCancelReturnRequest: data.canCancelReturnRequest,
      allowOnlyUniqueReturnRequest: data.allowOnlyUniqueReturnRequest,
      refundInitialShippingRule: data.refundInitialShippingRule,
      oneResolutionPerReturn: data.oneResolutionPerReturn,
      groupItemsByFulfillment: data.groupItemsByFulfillment,
      refundInitialShippingAmountRule: data.refundInitialShippingAmountRule,
      retractationEnabled: data.retractationEnabled,
      retractationFallbackEnabled: data.retractationFallbackEnabled,
      retractationFallbackDelayDays: data.retractationFallbackDelayDays,
    });
  };

  return (
    <CustomerPortalBasicsForm
      onSubmit={onSubmit}
      workflowLogisticianActivated={shop.workflowLogistician.activated}
      isRetractionOnly={isRetractionOnly}
      defaultValues={{
        contactLink: shop.preferences.contactLink,
        languages: shop.preferences.languages,
        webOrderOnly: shop.preferences.webOrderOnly,
        canCancelReturnRequest: shop.preferences.canCancelReturnRequest,
        allowOnlyUniqueReturnRequest:
          shop.preferences.allowOnlyUniqueReturnRequest,
        nonReturnableMetafieldItems:
          shop.preferences.nonReturnableMetafieldItems,
        nonReturnableItems: shop.preferences.nonReturnableItems,
        tagsForNotAcceptedOrder: shop.preferences.tagsForNotAcceptedOrder,
        restockOnReturn: shop.preferences.restockOnReturn,
        restockLocationId: shop.preferences.restockLocationId ?? null,
        autoTreateOnLogisticianValidate:
          shop.preferences.autoTreateOnLogisticianValidate,
        clientCanUpdatePersonalInformation:
          shop.preferences.clientCanUpdatePersonalInformation,
        refundInitialShippingRule: shop.preferences.refundInitialShippingRule,
        oneResolutionPerReturn: shop.preferences.oneResolutionPerReturn,
        singleArticleReturnOnly: shop.preferences.singleArticleReturnOnly,
        groupItemsByFulfillment: shop.preferences.groupItemsByFulfillment,
        refundInitialShippingAmountRule:
          shop.preferences.refundInitialShippingAmountRule,
        retractationEnabled: shop.preferences.retractationEnabled,
        retractationFallbackEnabled:
          shop.preferences.retractationFallbackEnabled,
        retractationFallbackDelayDays:
          shop.preferences.retractationFallbackDelayDays,
      }}
    >
      <div className="mt-8">
        <SettingsFormSectionHeader
          title={t("settings.config.customerSegments.title")}
          id={"customer-segments"}
        />
      </div>
      {isRetractionOnly ? (
        <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
          <div className="pointer-events-none select-none p-6 space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <div className="h-5 w-16 rounded-full bg-pink-100" />
                <div className="h-5 w-24 rounded-full bg-blue-100" />
                <div className="h-5 w-20 rounded-full bg-green-100" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-3/4 rounded bg-muted" />
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-2/3 rounded bg-muted" />
            </div>
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
              <LockClosedIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-sm font-semibold text-foreground">
              Segments clients
            </p>
            <p className="text-xs text-muted-foreground max-w-sm">
              Définissez des règles pour adapter le comportement du portail
              selon vos segments de clientèle.
            </p>
          </div>
        </div>
      ) : (
        <Card className="w-full">
          <CardContent className="pt-6">
            <CustomerSegmentsForm />
          </CardContent>
        </Card>
      )}
    </CustomerPortalBasicsForm>
  );
}
